import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "report" }
const _hoisted_2 = { class: "main" }
const _hoisted_3 = { class: "left-menu" }

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import debounce from "lodash-es/debounce";
import store from "@/store";
// config
import { DEFAULT_PAGE_STYLE } from "./config";
// utiles
import { bsonId } from "@/common/utils/common";
import { paginateDelta, quillDeltaToHtml } from "@/common/utils/quill";
import { paginateText } from "@/common/utils/text";
import { styleToNumber } from "@/common/utils/style";
import { toast } from "@/common/utils/toast";
import { generatePDF } from "@/common/utils/pdf";
// types
import type {
  ReportType,
  ReportPageType,
  ReportBlockType,
  ReportBlockPayloadType,
} from "@/common/types/report";
// rest
import { reportShow, reportPatch } from "@/common/api/rest/report/report";
import {
  reportBlockCreate,
  reportBlockList,
  reportBlockPatch,
  reportBlockReorder,
  reportBlockDelete,
} from "@/common/api/rest/report/block";
// components
import { baseConfirm, fullLoading } from "@/common/components";
import Header from "./components/header.vue";
import Menu from "./components/menu.vue";
import Page from "./components/page.vue";
import frontCover from "./components/front-cover.vue";
import SummaryPage from "./components/summary.vue";

interface SummaryItemType {
  id: string;
  label: string;
  page: number;
}

interface SummaryPageType {
  id: string;
  style: any;
  items: SummaryItemType[];
}


export default /*@__PURE__*/_defineComponent({
  __name: '[report]',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

// Container reutilizável para medições
let measureContainer: HTMLDivElement | null = null;

const zoom = ref(0.8);

const report = ref<ReportType | null>(null);
const blocks = ref<ReportBlockType[]>([]);
const pages = ref<ReportPageType[]>([]);
const selectedPage = ref<number | null>(null);
const summary = ref<SummaryItemType[]>([]);
const summaryPages = ref<SummaryPageType[]>([]);

const block = ref<ReportBlockType | null>(null);
const selectedBlock = ref<string | null>(null);
const typeMenu = ref<string | null>(null);
const keyMenu = ref(0);

const showBlockRemoveConfirm = ref(false);

// ============================================
// SISTEMA DE CACHE E OTIMIZAÇÃO
// ============================================
const heightCache = ref<Map<string, { height: number; timestamp: number }>>(
  new Map(),
);
const lastBlocksHash = ref<string>("");
const isPaginating = ref(false);

const paginateParams = ref({
  page: 0,
  pages: 1,
  limit: 10,
});

const isLoadingBlocks = ref(false);
const blocksLoaded = ref(false);

const accountId = computed(() => route.params.id as string);
const reportId = computed(() => route.params.report as string);

// ============================================
// HELPERS DEFENSIVOS (delta pode ser null)
// ============================================
const EMPTY_DELTA = { ops: [{ insert: "\n" }] };

function normalizeDelta(content: any): any {
  if (!content) return EMPTY_DELTA;
  if (typeof content === "string") {
    const trimmed = content.trim();
    if (!trimmed) return EMPTY_DELTA;
    if (trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === "object" && "ops" in parsed) {
          return parsed;
        }
      } catch {
        return EMPTY_DELTA;
      }
    }
    return { ops: [{ insert: trimmed }] };
  }
  if (typeof content === "object" && "ops" in content) {
    return content;
  }
  return EMPTY_DELTA;
}

function safeDeltaToHtml(content: any): string {
  const normalized = normalizeDelta(content);
  try {
    return quillDeltaToHtml(normalized);
  } catch (error) {
    console.error("safeDeltaToHtml error", error);
    return "";
  }
}

function getMeasureContainer(): HTMLDivElement {
  if (!measureContainer) {
    measureContainer = document.createElement("div");
    measureContainer.style.position = "absolute";
    measureContainer.style.visibility = "hidden";
    measureContainer.style.left = "-9999px";
    measureContainer.style.top = "-9999px";
    measureContainer.style.pointerEvents = "none";
    document.body.appendChild(measureContainer);
  }
  return measureContainer;
}

function getBlocksHash(): string {
  return blocks.value
    .map((b) => {
      const contentStr = JSON.stringify(b.content || "");
      const styleStr = JSON.stringify(b.style || "");
      return `${b.block_id}-${b.type}-${contentStr}-${styleStr}`;
    })
    .join("|");
}

function forceHashUpdate() {
  lastBlocksHash.value = getBlocksHash();
}

function shouldRepaginate(force = false): boolean {
  if (force) {
    return true;
  }
  const currentHash = getBlocksHash();
  const shouldUpdate = currentHash !== lastBlocksHash.value;
  if (shouldUpdate) {
    lastBlocksHash.value = currentHash;
  }
  return shouldUpdate;
}

function calculateElementHeight(
  cacheKey: string,
  createElement: () => HTMLElement,
): number {
  const CACHE_TTL = 10000;
  const now = Date.now();

  const cached = heightCache.value.get(cacheKey);
  if (cached && now - cached.timestamp < CACHE_TTL) {
    return cached.height;
  }

  const container = getMeasureContainer();
  const element = createElement();

  container.appendChild(element);
  const height = element.offsetHeight;
  container.removeChild(element);

  heightCache.value.set(cacheKey, { height, timestamp: now });

  return height;
}

function invalidateBlockCache(blockId: string) {
  const keysToDelete: string[] = [];
  heightCache.value.forEach((_, key) => {
    if (key.includes(blockId)) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach((key) => heightCache.value.delete(key));
}

// ============================================
// PAGINAÇÃO OTIMIZADA
// ============================================
function paginate(force = false) {
  if (isPaginating.value) {
    return;
  }

  if (!force && !shouldRepaginate() && pages.value.length > 0) {
    return;
  }

  isPaginating.value = true;

  const internalPages: ReportPageType[] = [];

  summary.value = [];
  summaryPages.value = [];

  let page = 0;
  let height = 0;

  const pageStyle = {
    ...DEFAULT_PAGE_STYLE,
  };

  const pageWidth = styleToNumber(pageStyle.width);
  const pageHeight = styleToNumber(pageStyle.height);
  const pagePadding = styleToNumber(pageStyle.padding);

  const AREA_WIDTH = pageWidth - pagePadding * 2;
  const AREA_HEIGHT = pageHeight - pagePadding * 2;

  internalPages[page] = {
    blocks: [],
    style: pageStyle,
    page_id: bsonId(),
  };

  const checkHeaderAndFooter = () => {
    if (report.value?.header?.visible) {
      height += 75;
    }

    if (report.value?.footer?.visible) {
      height += 60;
    }
  };

  checkHeaderAndFooter();

  const createNewPage = () => {
    page += 1;
    height = 0;

    checkHeaderAndFooter();

    internalPages[page] = {
      blocks: [],
      style: pageStyle,
      page_id: bsonId(),
    };
  };

  const calculateSpacing = (style: any) => {
    const safeStyle = style ?? {};
    const marginTop = styleToNumber(safeStyle["margin-top"] || "0px");
    const marginBottom = styleToNumber(safeStyle["margin-bottom"] || "0px");
    const paddingTop = styleToNumber(safeStyle["padding-top"] || "0px");
    const paddingBottom = styleToNumber(safeStyle["padding-bottom"] || "0px");
    const concat = 10;

    return {
      top: marginTop + paddingTop,
      bottom: marginBottom + paddingBottom,
      total: marginTop + marginBottom + paddingTop + paddingBottom + concat,
    };
  };

  const textPaginate = (
    text: string,
    availableHeight: number,
    style: {
      font_size: string;
      font_family: string;
      font_weight: string;
      line_height: string;
      text_align: string;
    },
  ) => {
    const result = paginateText(text, {
      width: `${AREA_WIDTH}px`,
      height: availableHeight,
      initial_height: height,
      font_size: style.font_size,
      font_family: style.font_family,
      font_weight: style.font_weight,
      line_height: style.line_height,
    });

    if (!result || result.length === 0) {
      return [{ text: text ?? "", height: 0 }];
    }

    return result;
  };

  // paginate blocks
  blocks.value.forEach((block) => {
    // paginate title
    if (block.type === "block-title") {
      const gapTitle = 20;
      const spacing = calculateSpacing(block.style);
      height += spacing.top + spacing.bottom + gapTitle;

      if (height >= AREA_HEIGHT) {
        createNewPage();
        height = spacing.top + spacing.bottom + gapTitle;
      }

      const normalizedContent = normalizeDelta(block.content);
      const textFull =
        normalizedContent?.ops?.map((op: any) => op.insert || "").join("") ||
        "";

      const texts = textPaginate(textFull, AREA_HEIGHT, {
        font_size: block.style?.["font-size"] || "16px",
        font_family: block.style?.["font-family"] || "Verdana",
        font_weight: block.style?.["font-weight"] || "normal",
        line_height: block.style?.["line-height"] || "normal",
        text_align: block.style?.["text-align"] || "left",
      });

      if (!texts || texts.length === 0) {
        internalPages[page].blocks.push({
          ...block,
          text: safeDeltaToHtml(block.content),
        });
        return;
      }

      if (texts[0].height < AREA_HEIGHT) {
        internalPages[page].blocks.push({
          ...block,
          text: safeDeltaToHtml(block.content),
        });
        height += texts[0].height;

        summary.value.push({
          id: block.block_id,
          label: textFull,
          page: page,
        });
      } else {
        createNewPage();
        internalPages[page].blocks.push({
          ...block,
          text: safeDeltaToHtml(block.content),
        });
        height = 0;
        height += texts[0].height;

        summary.value.push({
          id: block.block_id,
          label: textFull,
          page: page,
        });
      }

      height += spacing.top + spacing.bottom + gapTitle;
    }

    // paginate text
    if (block.type === "block-text") {
      const gapText = 6;
      const spacing = calculateSpacing(block.style);
      height += spacing.top + gapText;

      if (height >= AREA_HEIGHT) {
        createNewPage();
        height = spacing.top + gapText;
      }

      const normalizedContent = normalizeDelta(block.content);

      let texts: any[] = [];
      try {
        texts = paginateDelta(normalizedContent, {
          width: `${AREA_WIDTH}px`,
          height: AREA_HEIGHT,
          initial_height: height,
          font_size: block.style?.["font-size"] || "16px",
          font_family: block.style?.["font-family"] || "Verdana",
          font_weight: block.style?.["font-weight"] || "normal",
          line_height: block.style?.["line-height"] || "normal",
          text_align: block.style?.["text-align"] || "left",
        });
      } catch (error) {
        console.error("paginateDelta error", error);
        texts = [];
      }

      if (!texts || texts.length === 0) {
        internalPages[page].blocks.push({
          ...block,
          text: safeDeltaToHtml(block.content),
        });
        height += spacing.bottom + gapText;
        return;
      }

      if (texts.length === 1) {
        internalPages[page].blocks.push({
          ...block,
          text: safeDeltaToHtml({ ops: texts[0]?.ops ?? [] }),
        });
        height = texts[0]?.height ?? height;
      } else {
        texts.forEach((textPart, index) => {
          if (index === 0) {
            internalPages[page].blocks.push({
              ...block,
              text: safeDeltaToHtml({ ops: textPart?.ops ?? [] }),
            });
            height = textPart?.height ?? height;
          } else {
            createNewPage();

            internalPages[page].blocks.push({
              ...block,
              text: safeDeltaToHtml({ ops: textPart?.ops ?? [] }),
            });
            height += textPart?.height ?? 0;
          }
        });
      }

      height += spacing.bottom + gapText;
    }

    if (block.type === "block-table") {
      const spacing = calculateSpacing(block.style);
      height += spacing.total + 5;

      if (height >= AREA_HEIGHT) {
        createNewPage();
        height = spacing.total;
      }

      const content = block.content ?? {};
      const title = content.title;
      const columns = content.columns ?? [];
      const rows = content.rows ?? [];

      let titleHeight = 0;
      if (title) {
        const cacheKey = `table-title-${block.block_id}`;
        titleHeight = calculateElementHeight(cacheKey, () => {
          const titleElement = document.createElement("div");
          titleElement.style.fontSize = block.style?.["font-size"] || "16px";
          titleElement.style.fontFamily =
            block.style?.["font-family"] || "Verdana";
          titleElement.style.fontWeight = "bold";
          titleElement.style.width = `${AREA_WIDTH}px`;
          titleElement.innerText = title;
          return titleElement;
        });
        titleHeight += 5;
      }

      const headerHeight = 50;

      const rowHeights = rows
        .filter((row: any) => row?.visible)
        .map((row: any, rowIndex: number) => {
          const cacheKey = `table-row-${block.block_id}-${rowIndex}`;
          const calculatedHeight = calculateElementHeight(cacheKey, () => {
            const rowElement = document.createElement("div");
            rowElement.style.width = `${AREA_WIDTH}px`;
            rowElement.style.display = "flex";
            rowElement.style.fontSize = block.style?.["font-size"] || "16px";
            rowElement.style.fontFamily =
              block.style?.["font-family"] || "Verdana";

            (row.items ?? [])
              .filter((item: any) => {
                const col = columns.find((c: any) => c.id === item.col);
                return item.visible && col?.visible;
              })
              .forEach((item: any) => {
                const cell = document.createElement("div");
                cell.style.flex = "1";
                cell.style.padding = "0.875em 0.5em";
                cell.innerText = item.value || "";
                rowElement.appendChild(cell);
              });

            return rowElement;
          });

          return {
            row,
            height: calculatedHeight,
          };
        });

      let currentTableRows: any[] = [];
      let currentTableHeight = height + titleHeight + headerHeight;
      let isFirstTablePart = true;

      rowHeights.forEach((rowData: any, index: number) => {
        const rowHeight = rowData.height;

        if (currentTableHeight + rowHeight <= AREA_HEIGHT) {
          currentTableRows.push(rowData.row);
          currentTableHeight += rowHeight;
        } else {
          if (currentTableRows.length > 0) {
            internalPages[page].blocks.push({
              ...block,
              content: {
                ...content,
                title: isFirstTablePart ? title : null,
                rows: currentTableRows,
              },
            });
            height = currentTableHeight;
          }

          createNewPage();
          isFirstTablePart = false;

          currentTableHeight = headerHeight + rowHeight;
          currentTableRows = [rowData.row];
          height = currentTableHeight;
        }

        if (index === rowHeights.length - 1 && currentTableRows.length > 0) {
          internalPages[page].blocks.push({
            ...block,
            content: {
              ...content,
              title: isFirstTablePart ? title : null,
              rows: currentTableRows,
            },
          });
          height = currentTableHeight;
        }
      });

      if (rowHeights.length === 0) {
        internalPages[page].blocks.push(block);
        height += titleHeight + headerHeight;
      }
    }

    if (block.type === "block-data") {
      const spacing = calculateSpacing(block.style);
      height += spacing.total + 40;

      if (height >= AREA_HEIGHT) {
        createNewPage();
        height = spacing.total;
      }

      const content = block.content;

      if (!content || !content.columns || !content.fields || !content.rows) {
        internalPages[page].blocks.push(block);
        return;
      }

      const { title, columns, fields, rows } = content;

      let titleHeight = 0;
      if (title) {
        const cacheKey = `data-title-${block.block_id}`;
        titleHeight = calculateElementHeight(cacheKey, () => {
          const titleElement = document.createElement("div");
          titleElement.style.fontSize = block.style?.["font-size"] || "16px";
          titleElement.style.fontFamily =
            block.style?.["font-family"] || "Verdana";
          titleElement.style.fontWeight = "bold";
          titleElement.style.width = `${AREA_WIDTH}px`;
          titleElement.innerText = title;
          titleElement.style.paddingInline = "14px";
          titleElement.style.paddingBlock = "4.8px";
          return titleElement;
        });
      }

      const visibleColumns = columns.filter((col: any) => col.visible);
      const visibleFields = fields.filter((f: any) => f.visible);

      if (visibleColumns.length === 0) {
        internalPages[page].blocks.push(block);
        height += titleHeight;
        return;
      }

      const IMAGE_HEIGHT = 150 * (3 / 4);

      const rowHeights = rows.map((rowFields: any, rowIndex: number) => {
        let maxHeight = 0;

        visibleColumns.forEach((col: any, colIndex: number) => {
          const cacheKey = `data-cell-${block.block_id}-${rowIndex}-${colIndex}`;

          const columnHeight = calculateElementHeight(cacheKey, () => {
            const cell = document.createElement("td");
            cell.style.border = "1px solid #dee2e6";
            cell.style.paddingInline = "14px";
            cell.style.paddingBlock = "8px";
            cell.style.verticalAlign = "top";
            cell.style.whiteSpace = "normal";
            cell.style.wordWrap = "break-word";
            cell.style.wordBreak = "break-word";
            cell.style.overflowWrap = "break-word";
            cell.style.textAlign = "left";

            const colWidth = AREA_WIDTH / visibleColumns.length;
            cell.style.width = `${colWidth}px`;

            const itemDiv = document.createElement("div");
            itemDiv.style.display = "grid";
            itemDiv.style.gridTemplateColumns = "minmax(0, 1fr) minmax(0, 3fr)";
            itemDiv.style.alignItems = "start";
            itemDiv.style.rowGap = "8px";
            itemDiv.style.columnGap = "16px";
            itemDiv.style.marginBlock = "8px";
            itemDiv.style.width = "100%";

            const columnFields = (rowFields ?? []).filter(
              (f: any) => f.col === col.id && f.visible,
            );

            columnFields.forEach((field: any) => {
              const fieldData = visibleFields.find(
                (vf: any) => vf.id === field.field,
              );

              if (field.is_image && field.value) {
                const imgContainer = document.createElement("div");
                imgContainer.style.width = "150px";
                imgContainer.style.height = `${IMAGE_HEIGHT}px`;
                imgContainer.style.gridColumn = "1 / -1";
                itemDiv.appendChild(imgContainer);
              } else {
                if (fieldData?.label) {
                  const labelDiv = document.createElement("div");
                  labelDiv.style.fontWeight = "400";
                  labelDiv.style.fontSize =
                    block.style?.["font-size"] || "16px";
                  labelDiv.style.fontFamily =
                    block.style?.["font-family"] || "Verdana";
                  labelDiv.style.wordBreak = "break-word";
                  labelDiv.style.overflowWrap = "break-word";
                  labelDiv.innerText = `${fieldData.label}:`;
                  itemDiv.appendChild(labelDiv);
                }

                const valueDiv = document.createElement("div");
                valueDiv.style.fontWeight = "700";
                valueDiv.style.fontSize = block.style?.["font-size"] || "16px";
                valueDiv.style.fontFamily =
                  block.style?.["font-family"] || "Verdana";
                valueDiv.style.wordBreak = "break-word";
                valueDiv.style.overflowWrap = "break-word";
                valueDiv.innerText = field.value || "-";
                itemDiv.appendChild(valueDiv);
              }
            });

            cell.appendChild(itemDiv);
            return cell;
          });

          maxHeight = Math.max(maxHeight, columnHeight);
        });

        const rowSpacing = 16;

        return {
          rowFields,
          height: maxHeight + rowSpacing,
        };
      });

      let currentDataRows: any[] = [];
      let currentDataHeight = height + titleHeight;
      let isFirstDataPart = true;

      rowHeights.forEach((rowData: any, index: number) => {
        const rowHeight = rowData.height;

        if (currentDataHeight + rowHeight <= AREA_HEIGHT) {
          currentDataRows.push(rowData.rowFields);
          currentDataHeight += rowHeight;
        } else {
          if (currentDataRows.length > 0) {
            internalPages[page].blocks.push({
              ...block,
              content: {
                ...content,
                title: isFirstDataPart ? title : null,
                rows: currentDataRows,
              },
            });
            height = currentDataHeight;
          }

          createNewPage();
          isFirstDataPart = false;

          currentDataHeight = rowHeight;
          currentDataRows = [rowData.rowFields];
          height = currentDataHeight;
        }

        if (index === rowHeights.length - 1 && currentDataRows.length > 0) {
          internalPages[page].blocks.push({
            ...block,
            content: {
              ...content,
              title: isFirstDataPart ? title : null,
              rows: currentDataRows,
            },
          });
          height = currentDataHeight;
        }
      });

      if (rowHeights.length === 0) {
        internalPages[page].blocks.push(block);
        height += titleHeight;
      }
    }
  });

  if (report.value?.summary?.visible) {
    const internalSummary: SummaryPageType[] = [];

    const SUMMARY_UTIL_WIDTH = 500;

    let summaryHeight = 0;
    let summaryPage = -1;

    const createSummaryPage = () => {
      summaryPage += 1;
      summaryHeight = 60;

      if (report.value?.header?.visible) {
        summaryHeight += 75;
      }

      if (report.value?.footer?.visible) {
        summaryHeight += 60;
      }

      internalSummary[summaryPage] = {
        id: bsonId(),
        style: pageStyle,
        items: [],
      };
    };

    createSummaryPage();

    summary.value.forEach((summaryItem: SummaryItemType) => {
      summaryHeight += 12;

      if (summaryHeight > AREA_HEIGHT) {
        createSummaryPage();
      }

      const texts = textPaginate(summaryItem.label, SUMMARY_UTIL_WIDTH, {
        font_size: report.value?.summary?.size || "16px",
        font_family: report.value?.summary?.font_family || "Verdana",
        text_align: report.value?.summary?.align || "left",
        font_weight: "normal",
        line_height: "normal",
      });

      if (!texts || texts.length === 0) {
        internalSummary[summaryPage].items.push(summaryItem);
        return;
      }

      if (texts[0].height < AREA_HEIGHT) {
        internalSummary[summaryPage].items.push(summaryItem);
        summaryHeight += texts[0].height;
      } else {
        createSummaryPage();
        internalSummary[summaryPage].items.push(summaryItem);
        summaryHeight += texts[0].height;
      }
    });

    summaryPages.value = [...internalSummary];
  }

  pages.value = [...internalPages];
  isPaginating.value = false;

  lastBlocksHash.value = getBlocksHash();

  nextTick(() => {
    if (selectedPage.value && selectedBlock.value) {
      const pagesEl = document.getElementById("reportView");

      const element = document.getElementById(
        `${selectedPage.value}-${selectedBlock.value}`,
      );

      if (pagesEl && element) {
        pagesEl.scrollTo({
          top:
            pagesEl.scrollTop +
            element.getBoundingClientRect().top -
            pagesEl.getBoundingClientRect().top -
            50,
        });
      }
    }
  });
}

// ============================================
// FUNÇÕES PRINCIPAIS
// ============================================

function close() {
  if (!report.value) return;

  router.push(
    `/${report.value.account_id}/cockpit/${report.value.evaluation_id}`,
  );
}

function reset() {
  selectedBlock.value = null;
  typeMenu.value = null;
}

function onKeyDown(keyEvent: KeyboardEvent) {
  if (keyEvent.key === "Escape") {
    reset();
  }
}

// REPORT
async function fetchReport() {
  await reportShow(accountId.value, reportId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        report.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function patchReport(data: Partial<ReportType>) {
  await reportPatch(accountId.value, reportId.value, data)
    .then(async (response) => {
      const { success, message } = response;

      if (success) {
        await fetchReport();
        paginate(true);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// BLOCKS
async function fetchBlocks(reset = false) {
  if (!reportId.value) return;

  if (isLoadingBlocks.value) return;

  isLoadingBlocks.value = true;

  try {
    if (reset) {
      blocks.value = [];
      paginateParams.value.page = 0;
      paginateParams.value.pages = 1;
      blocksLoaded.value = false;
    }

    const collected: ReportBlockType[] = [];
    let currentPage = reset ? 0 : paginateParams.value.page;
    let totalPages = paginateParams.value.pages;

    const MAX_PAGES = 100;
    let iterations = 0;

    let keepFetching = true;

    while (keepFetching && iterations < MAX_PAGES) {
      const response = await reportBlockList(accountId.value, {
        report_id: reportId.value,
        page: currentPage,
        limit: paginateParams.value.limit,
      });

      const { success, data, message } = response;

      if (!success) {
        toast({ message: message, type: "error" });
        break;
      }

      if (Array.isArray(data?.list) && data.list.length > 0) {
        collected.push(...data.list);
      }

      totalPages = data?.infos?.pages ?? 1;

      currentPage += 1;
      iterations += 1;

      if (currentPage >= totalPages) {
        keepFetching = false;
      }

      if (!data?.list || data.list.length === 0) {
        keepFetching = false;
      }
    }

    const uniqueMap = new Map<string, ReportBlockType>();

    [...blocks.value, ...collected].forEach((b) => {
      if (b?.block_id) {
        uniqueMap.set(b.block_id, b);
      }
    });

    const merged = Array.from(uniqueMap.values());
    merged.sort((a, b) => (a.ordered ?? 0) - (b.ordered ?? 0));

    blocks.value = merged;

    paginateParams.value.page = currentPage;
    paginateParams.value.pages = totalPages;
    blocksLoaded.value = currentPage >= totalPages;

    if (blocksLoaded.value) {
      paginate(true);
    }
  } catch (error) {
    toast({ message: t("error.default"), type: "error" });
  } finally {
    isLoadingBlocks.value = false;
  }
}

function blockSelect(data: { page_index: number; block: ReportBlockType }) {
  block.value = data.block;
  keyMenu.value++;
  selectedBlock.value = data.block.block_id;
  typeMenu.value = data.block.type;
  selectedPage.value = data.page_index;
}

function blockAddConfirm(data: { content: ReportBlockType; key: string }) {
  if (!reportId.value) {
    return;
  }

  const payload: ReportBlockPayloadType = {
    content: data.content.content,
    style: data.content.style,
    type: data.content.type,
    ordered: blocks.value.length,
    visible: true,
  };

  reportBlockCreate(accountId.value, {
    ...payload,
    evaluation_id: report.value?.evaluation_id,
    report_id: reportId.value,
  })
    .then((response) => {
      const { success, message, data } = response;

      if (success) {
        toast({ message: message, type: "success" });
        blocks.value.push(data);
        debouncePaginate(true);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      typeMenu.value = null;
    });
}

function blockPatch(data: {
  block_id: string;
  payload: Partial<ReportBlockType>;
}) {
  if (!reportId.value) {
    return;
  }

  reportBlockPatch(accountId.value, data.block_id, data.payload)
    .then((response) => {
      const { success, message } = response;

      if (!success) {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function blockUpdate(data: {
  block_id: string;
  payload: Partial<ReportBlockType>;
}) {
  const blockIndex = blocks.value.findIndex(
    (b) => b.block_id === data.block_id,
  );

  if (blockIndex < 0) {
    return;
  }

  const newblock = {
    ...blocks.value[blockIndex],
    ...data.payload,
  };

  block.value = { ...newblock };
  blocks.value.splice(blockIndex, 1, newblock);

  invalidateBlockCache(data.block_id);

  debouncePaginate(true);
}

function blockReorder() {
  if (!reportId.value) {
    return;
  }

  const payload = blocks.value?.map((block) => block.block_id) ?? [];

  reportBlockReorder(accountId.value, reportId.value, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchBlocks(true);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function blockMoveUp(blockId: string) {
  const blockIndex = blocks.value.findIndex((b) => b.block_id === blockId);

  if (blockIndex === -1) {
    return;
  }

  const block = blocks.value.splice(blockIndex, 1)[0];
  blocks.value.splice(blockIndex - 1, 0, block);

  blockReorder();
}

function blockMoveDown(blockId: string) {
  const blockIndex = blocks.value.findIndex((b) => b.block_id === blockId);

  if (blockIndex === -1) {
    return;
  }

  const block = blocks.value.splice(blockIndex, 1)[0];
  blocks.value.splice(blockIndex + 1, 0, block);

  blockReorder();
}

function blockRemove(blockId: string) {
  const blockIndex = blocks.value.findIndex((b) => b.block_id === blockId);

  if (blockIndex === -1) {
    return;
  }

  selectedBlock.value = blocks.value[blockIndex].block_id;
  showBlockRemoveConfirm.value = true;
}

function blockRemoveConfirm() {
  if (!selectedBlock.value || !reportId.value) {
    return;
  }

  reportBlockDelete(accountId.value, selectedBlock.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        blocks.value = blocks.value.filter(
          (b) => b.block_id !== selectedBlock.value,
        );

        blockRemoveCancel();
        debouncePaginate(true);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      typeMenu.value = null;
    });
}

function blockRemoveCancel() {
  showBlockRemoveConfirm.value = false;
  selectedBlock.value = null;
}

async function printPage() {
  reset();

  store.commit("SET_REPORT_SCALE", true);
  store.commit("SET_LOADING", true);
  store.commit("SET_PRINT", true);

  const zoomBackup = zoom.value + 0;
  zoom.value = 1;

  const element = document.getElementById("reportView");
  if (!element) {
    store.commit("SET_REPORT_SCALE", null);
    store.commit("SET_LOADING", false);
    return;
  }

  await new Promise((resolve) => {
    setTimeout(async () => {
      resolve(true);
    }, 1000);
  });

  await generatePDF(element, `relatório.pdf`);

  setTimeout(async () => {
    store.commit("SET_REPORT_SCALE", null);
    store.commit("SET_LOADING", false);
    store.commit("SET_PRINT", false);
    zoom.value = zoomBackup;
  }, 1000);
}

const debounceBlockPatch = debounce(blockPatch, 800);
const debouncePaginate = debounce((force = false) => {
  paginate(force);
}, 1200);

const clearCacheInterval = setInterval(() => {
  const now = Date.now();
  const CACHE_TTL = 60000;

  heightCache.value.forEach((value, key) => {
    if (now - value.timestamp > CACHE_TTL) {
      heightCache.value.delete(key);
    }
  });
}, 60000);

function checkWindowSize() {
  if (window.innerWidth < 1024) {
    close();
  }
}

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeyDown);
  window.removeEventListener("resize", checkWindowSize);

  if (measureContainer) {
    document.body.removeChild(measureContainer);
    measureContainer = null;
  }

  clearInterval(clearCacheInterval);

  heightCache.value.clear();
});

onMounted(async () => {
  checkWindowSize();
  store.commit("SET_LOADING", true);
  await fetchReport();
  await fetchBlocks(true);
  store.commit("SET_LOADING", false);

  document.addEventListener("keydown", onKeyDown);
  window.addEventListener("resize", checkWindowSize);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("article", _hoisted_1, [
    _createVNode(Header, {
      zoom: zoom.value,
      "onUpdate:zoom": _cache[0] || (_cache[0] = ($event: any) => (zoom.value = $event)),
      onClose: _cache[1] || (_cache[1] = ($event: any) => (close())),
      onDownload: _cache[2] || (_cache[2] = ($event: any) => (printPage()))
    }, null, 8, ["zoom"]),
    _createElementVNode("main", _hoisted_2, [
      _createElementVNode("section", _hoisted_3, [
        _createVNode(Menu, {
          block: block.value,
          report: report.value,
          type: typeMenu.value,
          "onBlock:add": blockAddConfirm,
          "onBlock:update": _cache[3] || (_cache[3] = ($event: any) => {
            blockUpdate($event);
            _unref(debounceBlockPatch)($event);
          }),
          "onReport:reload": _cache[4] || (_cache[4] = ($event: any) => (fetchReport())),
          "onReport:update": _cache[5] || (_cache[5] = ($event: any) => (patchReport($event))),
          onClose: _cache[6] || (_cache[6] = ($event: any) => (reset())),
          "onSelect:type": _cache[7] || (_cache[7] = ($event: any) => (typeMenu.value = $event))
        }, null, 8, ["block", "report", "type"])
      ]),
      _createElementVNode("section", {
        id: "reportView",
        class: _normalizeClass(["pages", {
          print: _unref(store).getters.reportScale,
        }])
      }, [
        (report.value?.front_cover?.visible)
          ? (_openBlock(), _createBlock(frontCover, {
              key: 0,
              company: report.value?.company,
              data: report.value?.front_cover,
              print: _unref(store).getters.reportScale,
              style: _normalizeStyle(_unref(DEFAULT_PAGE_STYLE)),
              zoom: zoom.value
            }, null, 8, ["company", "data", "print", "style", "zoom"]))
          : _createCommentVNode("", true),
        (report.value?.summary?.visible)
          ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(summaryPages.value, (item, index) => {
              return (_openBlock(), _createBlock(SummaryPage, {
                key: index,
                avatar: report.value?.avatar,
                footer: report.value?.footer,
                header: report.value?.header,
                company: report.value?.company,
                data: item,
                summary: report.value?.summary,
                print: _unref(store).getters.reportScale,
                style: _normalizeStyle(_unref(DEFAULT_PAGE_STYLE)),
                zoom: zoom.value
              }, null, 8, ["avatar", "footer", "header", "company", "data", "summary", "print", "style", "zoom"]))
            }), 128))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pages.value, (page, pageIndex) => {
          return (_openBlock(), _createBlock(Page, {
            key: page.page_id,
            page: page,
            "page-index": pageIndex,
            "account-id": report.value?.account_id,
            avatar: report.value?.avatar,
            footer: report.value?.footer,
            header: report.value?.header,
            company: report.value?.company,
            "selected-block": selectedBlock.value,
            zoom: zoom.value,
            print: _unref(store).getters.reportScale,
            "onBlock:moveDown": blockMoveDown,
            "onBlock:moveUp": blockMoveUp,
            "onBlock:remove": blockRemove,
            "onBlock:reorder": blockReorder,
            "onBlock:select": blockSelect,
            "onBlock:update": _cache[8] || (_cache[8] = ($event: any) => {
              blockUpdate($event);
              _unref(debounceBlockPatch)($event);
            })
          }, null, 8, ["page", "page-index", "account-id", "avatar", "footer", "header", "company", "selected-block", "zoom", "print"]))
        }), 128))
      ], 2)
    ]),
    _createVNode(_unref(baseConfirm), {
      show: showBlockRemoveConfirm.value,
      title: "REMOVER BLOCO",
      message: `Tem certeza que deseja remover o bloco?`,
      onCancel: blockRemoveCancel,
      onConfirm: blockRemoveConfirm
    }, null, 8, ["show"]),
    (_unref(store).getters.loading)
      ? (_openBlock(), _createBlock(_unref(fullLoading), { key: 0 }))
      : _createCommentVNode("", true)
  ]))
}
}

})